import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import {
  TAB_OPTIONS,
  INTERNAL_ROUTE_KEYS,
  LINK_TYPES,
  CARD_STATUS,
  SCROLL_SEQUENCE_MIN,
  SCROLL_SEQUENCE_MAX,
  normalizeTabs,
  generateCardId
} from './PromotionsData';

// Red asterisk marker for required fields.
const Req = () => <span style={{ color: '#dc3545' }}> *</span>;

const EMPTY_CARD = () => ({
  id: '',
  cardName: '',
  cardName_hin: '',
  cardName_mar: '',
  cardName_guj: '',
  cardImage: '',
  cardSequence: 1,
  status: 'ACTIVE',
  // Two admin inputs; one wins on save. `linkUrl` takes precedence over `linkRoute` when both filled.
  link: { route: '', url: '', params: '{}' }
});

const EMPTY_STATE = {
  section_name: '',
  section_name_hin: '',
  section_name_mar: '',
  section_name_guj: '',
  tabs: [],
  scroll_sequence: 0,
  active: 1,
  explore_type: 'INTERNAL',
  explore_target: '',
  explore_params: '{}',
  cards: [EMPTY_CARD()]
};

const hydrateFromRow = (row) => {
  if (!row) return { ...EMPTY_STATE };
  const explore = row.explore_all || {};
  return {
    section_name: row.section_name || '',
    section_name_hin: row.section_name_hin || '',
    section_name_mar: row.section_name_mar || '',
    section_name_guj: row.section_name_guj || '',
    tabs: normalizeTabs(row.tabs),
    scroll_sequence: row.scroll_sequence ?? 0,
    active: row.active ? 1 : 0,
    explore_type: explore.type || 'INTERNAL',
    explore_target: explore.target || '',
    explore_params: JSON.stringify(explore.params || {}, null, 0),
    cards: (Array.isArray(row.cards) && row.cards.length > 0)
      ? row.cards.map(c => {
          const raw = c.link?.target || '';
          // If the stored target matches an enum route key → prefill the route dropdown.
          // Otherwise treat it as a free-text URL/deeplink.
          const isRoute = INTERNAL_ROUTE_KEYS.includes(raw);
          return {
            id: c.id || '',
            cardName: c.cardName || '',
            cardName_hin: c.cardName_hin || '',
            cardName_mar: c.cardName_mar || '',
            cardName_guj: c.cardName_guj || '',
            cardImage: c.cardImage || '',
            cardSequence: c.cardSequence ?? 1,
            status: c.status || 'ACTIVE',
            link: {
              route: isRoute ? raw : '',
              url: isRoute ? '' : raw,
              params: JSON.stringify(c.link?.params || {}, null, 0)
            }
          };
        })
      : [EMPTY_CARD()]
  };
};

const parseJSON = (raw, fallback) => {
  if (!raw || !raw.trim()) return fallback;
  try { return JSON.parse(raw); } catch (e) { return fallback; }
};

// Infer link.type from URL scheme: http(s) → EXTERNAL, anything else (custom scheme, deeplink) → INTERNAL.
const inferLinkType = (target) => {
  const t = String(target || '').trim().toLowerCase();
  return t.startsWith('http://') || t.startsWith('https://') ? 'EXTERNAL' : 'INTERNAL';
};

const buildPayload = (s) => ({
  section_name: s.section_name,
  section_name_hin: s.section_name_hin,
  section_name_mar: s.section_name_mar,
  section_name_guj: s.section_name_guj,
  tabs: s.tabs,
  scroll_sequence: Number(s.scroll_sequence),
  active: s.active ? 1 : 0,
  explore_all: {
    type: s.explore_type,
    target: s.explore_target,
    params: parseJSON(s.explore_params, {})
  },
  cards: s.cards.map(c => {
    // URL wins if both filled; otherwise fall back to the route dropdown value.
    const url = (c.link.url || '').trim();
    const route = (c.link.route || '').trim();
    const target = url || route;
    const type = url ? inferLinkType(url) : 'INTERNAL';
    return {
      id: c.id || generateCardId(c.cardName),
      cardSequence: Number(c.cardSequence),
      cardName: c.cardName,
      cardName_hin: c.cardName_hin,
      cardName_mar: c.cardName_mar,
      cardName_guj: c.cardName_guj,
      cardImage: c.cardImage,
      status: c.status,
      link: {
        type,
        target,
        params: parseJSON(c.link.params, {})
      }
    };
  })
});

const validate = (s) => {
  const errors = [];
  if (!s.section_name?.trim())     errors.push('Section name (English) is required.');
  if (!s.section_name_hin?.trim()) errors.push('Section name (Hindi) is required.');
  if (!s.section_name_mar?.trim()) errors.push('Section name (Marathi) is required.');
  if (!s.section_name_guj?.trim()) errors.push('Section name (Gujarati) is required.');
  if (!Array.isArray(s.tabs) || s.tabs.length === 0) errors.push('At least one tab must be selected.');
  else {
    const invalid = s.tabs.filter(t => !TAB_OPTIONS.includes(t));
    if (invalid.length) errors.push(`Invalid tab value(s): ${invalid.join(', ')}`);
    const uniq = new Set(s.tabs);
    if (uniq.size !== s.tabs.length) errors.push('Duplicate tab values not allowed.');
  }
  const seq = Number(s.scroll_sequence);
  if (isNaN(seq) || seq < SCROLL_SEQUENCE_MIN || seq > SCROLL_SEQUENCE_MAX) {
    errors.push(`Scroll sequence must be an integer between ${SCROLL_SEQUENCE_MIN} and ${SCROLL_SEQUENCE_MAX}.`);
  }
  if (!s.explore_target?.trim()) errors.push('Explore All target is required.');

  if (!Array.isArray(s.cards) || s.cards.length === 0) {
    errors.push('At least one card is required.');
  } else {
    s.cards.forEach((c, idx) => {
      if (!c.cardImage?.trim()) errors.push(`Card #${idx + 1}: image URL is required.`);
      if (!c.cardName?.trim())     errors.push(`Card #${idx + 1}: name (English) is required.`);
      if (!c.cardName_hin?.trim()) errors.push(`Card #${idx + 1}: name (Hindi) is required.`);
      if (!c.cardName_mar?.trim()) errors.push(`Card #${idx + 1}: name (Marathi) is required.`);
      if (!c.cardName_guj?.trim()) errors.push(`Card #${idx + 1}: name (Gujarati) is required.`);
      const hasRoute = c.link?.route?.trim();
      const hasUrl = c.link?.url?.trim();
      if (!hasRoute && !hasUrl) {
        errors.push(`Card #${idx + 1}: pick a Link Target or paste a Deeplink / URL.`);
      }
      if (!c.status) errors.push(`Card #${idx + 1}: status is required.`);
      if (c.cardSequence === '' || isNaN(Number(c.cardSequence))) {
        errors.push(`Card #${idx + 1}: sequence must be a number.`);
      }
    });
    const seqs = s.cards.map(c => Number(c.cardSequence));
    if (new Set(seqs).size !== seqs.length) {
      errors.push('Duplicate cardSequence values within this section are not allowed.');
    }
  }

  return { ok: errors.length === 0, errors };
};

const PromotionsForm = ({ show, onHide, mode, initialRow }) => {
  const isEdit = mode === 'edit';
  const [state, setState] = useState(EMPTY_STATE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setState(initialRow ? hydrateFromRow(initialRow) : { ...EMPTY_STATE });
  }, [show, initialRow]);

  const setField = (name, value) => setState(prev => ({ ...prev, [name]: value }));

  const toggleTab = (tab) => {
    setState(prev => {
      const has = prev.tabs.includes(tab);
      return { ...prev, tabs: has ? prev.tabs.filter(t => t !== tab) : [...prev.tabs, tab] };
    });
  };

  const updateCard = (idx, patch) => {
    setState(prev => ({
      ...prev,
      cards: prev.cards.map((c, i) => (i === idx ? { ...c, ...patch } : c))
    }));
  };

  const updateCardLink = (idx, patch) => {
    setState(prev => ({
      ...prev,
      cards: prev.cards.map((c, i) => (i === idx ? { ...c, link: { ...c.link, ...patch } } : c))
    }));
  };

  const addCard = () => {
    setState(prev => {
      const nextSeq = prev.cards.length
        ? Math.max(...prev.cards.map(c => Number(c.cardSequence) || 0)) + 1
        : 1;
      return { ...prev, cards: [...prev.cards, { ...EMPTY_CARD(), cardSequence: nextSeq }] };
    });
  };

  const removeCard = (idx) => {
    setState(prev => {
      if (prev.cards.length <= 1) {
        toast.info('At least one card is required.');
        return prev;
      }
      return { ...prev, cards: prev.cards.filter((_, i) => i !== idx) };
    });
  };

  // Move a card up/down. Also swap cardSequence values so ordering is persisted server-side.
  const moveCard = (idx, dir) => {
    setState(prev => {
      const cards = [...prev.cards];
      const target = idx + dir;
      if (target < 0 || target >= cards.length) return prev;
      const a = { ...cards[idx] };
      const b = { ...cards[target] };
      const aSeq = a.cardSequence;
      a.cardSequence = b.cardSequence;
      b.cardSequence = aSeq;
      cards[idx] = b;
      cards[target] = a;
      return { ...prev, cards };
    });
  };

  const handleSubmit = async () => {
    const { ok, errors } = validate(state);
    if (!ok) {
      errors.forEach(e => toast.error(e));
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(state);
      let res;
      if (isEdit) {
        res = await _axios.put(`${ServerPath}/promotions/admin/${initialRow.id}`, payload);
      } else {
        res = await _axios.post(`${ServerPath}/promotions/admin`, payload);
      }
      if (res.data?.success) {
        toast.success(isEdit ? 'Promotion updated.' : 'Promotion created.');
        onHide(true);
      } else {
        toast.error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit Promotion #${initialRow?.id}` : 'Create Promotion'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Section-level */}
          <div className="row g-3 mb-3">
            <div className="col-md-8">
              <Form.Label>Scroll Sequence<Req /> <span className="text-muted small">(0–999)</span></Form.Label>
              <Form.Control
                type="number"
                min={SCROLL_SEQUENCE_MIN}
                max={SCROLL_SEQUENCE_MAX}
                value={state.scroll_sequence}
                onChange={(e) => setField('scroll_sequence', e.target.value)}
              />
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <Form.Check
                type="switch"
                id="active-switch"
                label={state.active ? 'Active' : 'Inactive'}
                checked={!!state.active}
                onChange={(e) => setField('active', e.target.checked ? 1 : 0)}
              />
            </div>
          </div>

          {/* Section Name — English / Hindi / Marathi / Gujarati side-by-side */}
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Form.Label>Section Name (English)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.section_name}
                onChange={(e) => setField('section_name', e.target.value)}
                placeholder="e.g. Devotion"
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Section Name (Hindi)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.section_name_hin}
                onChange={(e) => setField('section_name_hin', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Section Name (Marathi)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.section_name_mar}
                onChange={(e) => setField('section_name_mar', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Section Name (Gujarati)<Req /></Form.Label>
              <Form.Control
                type="text"
                value={state.section_name_guj}
                onChange={(e) => setField('section_name_guj', e.target.value)}
              />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-12">
              <Form.Label>Display Tabs<Req /> <span className="text-muted small">(multi-select)</span></Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {TAB_OPTIONS.map(tab => (
                  <Form.Check
                    key={tab}
                    type="checkbox"
                    id={`tab-${tab}`}
                    label={tab}
                    checked={state.tabs.includes(tab)}
                    onChange={() => toggleTab(tab)}
                  />
                ))}
              </div>
            </div>
          </div>

          <h6 className="mb-2 mt-3">Explore All</h6>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <Form.Label>Type<Req /></Form.Label>
              <Form.Select
                value={state.explore_type}
                onChange={(e) => setField('explore_type', e.target.value)}
              >
                {LINK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-9">
              <Form.Label>Target<Req /></Form.Label>
              {state.explore_type === 'INTERNAL' ? (
                <Form.Select
                  value={state.explore_target}
                  onChange={(e) => setField('explore_target', e.target.value)}
                >
                  <option value="">Select a route</option>
                  {INTERNAL_ROUTE_KEYS.map(r => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              ) : (
                <Form.Control
                  type="url"
                  value={state.explore_target}
                  onChange={(e) => setField('explore_target', e.target.value)}
                  placeholder="https://..."
                />
              )}
            </div>
          </div>

          <div className="d-flex align-items-center mb-2 mt-3">
            <h6 className="mb-0 me-auto">Cards ({state.cards.length})</h6>
            <Button size="sm" variant="outline-primary" onClick={addCard}>+ Add card</Button>
          </div>

          {state.cards.map((card, idx) => (
            <div key={idx} className="border rounded p-3 mb-2 bg-light">
              <div className="d-flex align-items-center mb-2">
                <strong className="me-auto text-dark">Card #{idx + 1}</strong>
                <Button size="sm" variant="outline-secondary" className="me-1" disabled={idx === 0} onClick={() => moveCard(idx, -1)}>↑</Button>
                <Button size="sm" variant="outline-secondary" className="me-1" disabled={idx === state.cards.length - 1} onClick={() => moveCard(idx, 1)}>↓</Button>
                <Button size="sm" variant="outline-danger" onClick={() => removeCard(idx)}>Remove</Button>
              </div>
              <div className="row g-2">
                <div className="col-md-3">
                  <Form.Label className="small text-dark">Card Name (English)<Req /></Form.Label>
                  <Form.Control
                    type="text"
                    value={card.cardName}
                    onChange={(e) => updateCard(idx, { cardName: e.target.value })}
                    placeholder="Teerthas"
                  />
                </div>
                <div className="col-md-3">
                  <Form.Label className="small text-dark">Card Name (Hindi)<Req /></Form.Label>
                  <Form.Control
                    type="text"
                    value={card.cardName_hin}
                    onChange={(e) => updateCard(idx, { cardName_hin: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <Form.Label className="small text-dark">Card Name (Marathi)<Req /></Form.Label>
                  <Form.Control
                    type="text"
                    value={card.cardName_mar}
                    onChange={(e) => updateCard(idx, { cardName_mar: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <Form.Label className="small text-dark">Card Name (Gujarati)<Req /></Form.Label>
                  <Form.Control
                    type="text"
                    value={card.cardName_guj}
                    onChange={(e) => updateCard(idx, { cardName_guj: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <Form.Label className="small text-dark">Sequence<Req /></Form.Label>
                  <Form.Control
                    type="number"
                    value={card.cardSequence}
                    onChange={(e) => updateCard(idx, { cardSequence: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <Form.Label className="small text-dark">Status<Req /></Form.Label>
                  <Form.Select
                    value={card.status}
                    onChange={(e) => updateCard(idx, { status: e.target.value })}
                  >
                    {CARD_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label className="small text-dark">Card Image URL<Req /></Form.Label>
                  <Form.Control
                    type="url"
                    value={card.cardImage}
                    onChange={(e) => updateCard(idx, { cardImage: e.target.value })}
                    placeholder="https://..."
                  />
                  {card.cardImage && (
                    <div className="mt-1 small">
                      <img
                        src={card.cardImage}
                        alt="card"
                        style={{ maxHeight: 32, verticalAlign: 'middle', border: '1px solid #eee' }}
                      />
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <Form.Label className="small text-dark">Link Target</Form.Label>
                  <Form.Select
                    value={card.link.route}
                    onChange={(e) => updateCardLink(idx, { route: e.target.value })}
                  >
                    <option value="">— none —</option>
                    {INTERNAL_ROUTE_KEYS.map(r => <option key={r} value={r}>{r}</option>)}
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label className="small text-dark">Or Deeplink / URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={card.link.url}
                    onChange={(e) => updateCardLink(idx, { url: e.target.value })}
                    placeholder="myparikrama://teerthas/123  or  https://external.com/page"
                  />
                  <Form.Text muted>Fill one. URL wins if both are set.</Form.Text>
                </div>
              </div>
            </div>
          ))}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromotionsForm;
